import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Feature, FeatureItem } from 'src/model';
import {
  GET_FEATURES,
  MILLER_HEIMAN_ROLE_BASE_URL,
  AI_SIGNAL_REFRESH_TOOGLE,
  UPDATE_CUSTOMER_WEB_TRACKING_STATUS,
  UPDATE_TENANT_VIEW,
} from 'src/constants/httpEndPoints';
import { useToast } from './useToast';
import { apiClient } from 'src/api/apiClient';
import { useProfile } from './useProfile';
import { setFeatureFlags } from 'src/store/login';
import { useDispatch } from 'react-redux';
import { transformFeaturesForRedux } from 'src/utils/utilityMethods';
import { IntegrationsTools } from 'src/features/settings/integrations/constants/integrations';
import { getConnectedCmpList } from 'src/store/run-campaign/run-campaign.action';
import { AppDispatch } from 'src/app/rootStore';
import { setAdminFeatureFlags } from 'src/store/login/adminfeature-flags.actions';
interface IFeaturesContext {
  features: Feature[];
  getFeatures: () => void;
  featureMutate: (feature_to_update: Feature, checked: boolean) => void;
  enableSproutsAdminFeature: (featureToUpdate: Feature, checked: boolean) => void;
  updateFeatures: () => void;
  isFeatureUpdated: boolean;
  getFeaturesByName: (featuresName: string[]) => {
    [key: string]: boolean;
  };
}
const FeaturesContext = createContext<IFeaturesContext>({} as IFeaturesContext);
interface Props {
  children?: React.ReactNode;
}
export const FeaturesProvider = ({ children }: Props) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [initialFeatures, setInitialFeatures] = useState<Feature[]>([]);
  const [isFeatureUpdated, setIsFeatureUpdated] = useState(false);
  const [orderedFeatures, setOrderedFeatures] = useState<Feature[]>([]);
  const toast = useToast();
  const { customerId, emailId } = useProfile();
  const timeoutIdRef = useRef<number | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
    // Clear any existing interval when tenant context changes
    if (refreshIntervalRef.current) window.clearInterval(refreshIntervalRef.current);

    timeoutIdRef.current = window.setTimeout(() => {
      if (emailId && customerId) {
        getFeatures();
        // Fetch connected campaigns independently of the features response.
        // The runCampaign slice is not persisted, so this must reload on every
        // mount; coupling it to features.length > 0 caused the Run Campaign
        // button to stay hidden until a manual refresh or re-login.
        dispatch(getConnectedCmpList({ emailId, customerId }));
      }
    }, 100);

    // Refresh feature flags periodically so toggles take effect without a redeploy or manual refresh.
    // Keep aligned with version checks (5 min) to avoid excessive load.
    if (emailId && customerId) {
      refreshIntervalRef.current = window.setInterval(() => {
        getFeatures();
      }, 1000 * 60 * 5);
    }

    return () => {
      if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
      if (refreshIntervalRef.current) window.clearInterval(refreshIntervalRef.current);
    };
  }, [customerId, emailId]);
  useEffect(() => {
    setIsFeatureUpdated(
      features.some(
        (feature, index) =>
          feature.enabled !== initialFeatures[index].enabled ||
          feature.isSproutsAdmin !== initialFeatures[index].isSproutsAdmin,
      ),
    );
  }, [features, initialFeatures]);

  const enableSproutsAdminFeature = (featureToUpdate: Feature, checked: boolean) => {
    const updatedFeatures = features.map((feature) => {
      if (feature.feature === featureToUpdate.feature) {
        return {
          ...feature,
          isSproutsAdmin: checked,
        };
      }
      return feature;
    });
    setFeatures(updatedFeatures);
  };

  const updateTheColumnsOrder = () => {
    apiClient
      .post(
        UPDATE_TENANT_VIEW,
        { features: orderedFeatures?.map((item: Feature) => item.feature) },
        {
          headers: {
            'X-Customer-Id': customerId,
            'X-Login-Email': emailId,
          },
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setOrderedFeatures([]);
        }
      });
  };

  const updateFeaturesInOrder = (featureToUpdate: Feature, checked: boolean) => {
    const webTrackingOrderedFeature = orderedFeatures.find(
      (feature) => feature.feature === 'Website Tracking',
    );
    if (!checked) {
      if (featureToUpdate.feature === 'Website Tracking') {
        const newArr = orderedFeatures.filter(
          (feature) =>
            feature.feature !== 'Website Tracking' &&
            feature.feature !== 'Website Visitors Dashboard',
        );
        setOrderedFeatures(newArr);
      } else {
        const newArr = orderedFeatures.filter(
          (feature) => feature.feature !== featureToUpdate.feature,
        );

        setOrderedFeatures(newArr);
      }
    } else {
      if (
        featureToUpdate.feature === 'Website Visitors Dashboard' &&
        webTrackingOrderedFeature?.enabled
      ) {
        setOrderedFeatures([...orderedFeatures, { ...featureToUpdate, enabled: checked }]);
      } else if (featureToUpdate.feature !== 'Website Visitors Dashboard') {
        setOrderedFeatures([...orderedFeatures, { ...featureToUpdate, enabled: checked }]);
      }
    }
  };

  const featureMutate = (feature_to_update: Feature, checked: boolean) => {
    updateFeaturesInOrder(feature_to_update, checked);
    const webTrackingFeature = features.find((feature) => feature.feature === 'Website Tracking');
    if (
      feature_to_update.feature === 'Website Visitors Dashboard' &&
      !webTrackingFeature?.enabled
    ) {
      const updatedFeatures = features.map((feature) => {
        if (feature.feature === 'Website Tracking Dashboard') {
          return {
            ...feature,
            enabled: checked,
          };
        }
        return feature;
      });
      setFeatures(updatedFeatures);
    } else if (feature_to_update.feature === 'Campaign Dashboard') {
      const updatedFeatures = features.map((feature) => {
        if (feature.feature === 'Campaign Dashboard') {
          return {
            ...feature,
            enabled: checked,
          };
        }
        return feature;
      });
      setFeatures(updatedFeatures);
    } else if (feature_to_update.feature === 'Website Tracking' && !checked) {
      const updatedFeatures = features.map((feature) => {
        if (
          feature.feature === 'Website Visitors Dashboard' ||
          feature.feature === 'Website Tracking'
        ) {
          return {
            ...feature,
            enabled: false,
          };
        }
        return feature;
      });
      setFeatures(updatedFeatures);
    } else {
      const updatedFeatures = features.map((feature) => {
        if (feature.feature === feature_to_update.feature) {
          return {
            ...feature,
            enabled: checked,
          };
        }
        if (
          checked &&
          feature_to_update.feature === 'External DB Search' &&
          feature.feature === 'Move To TAL over Kafka' &&
          !feature.enabled
        ) {
          return {
            ...feature,
            enabled: true,
          };
        }
        const crmFeatures = Object.values(IntegrationsTools)
          .filter((integration) => integration.type === 'CRM')
          .map((integration) => integration.featureFlag);
        const cmpFeatures = Object.values(IntegrationsTools)
          .filter((integration) => integration.type === 'CMP')
          .map((integration) => integration.featureFlag);

        // If enabling a CRM feature, disable all other CRM features
        if (
          checked &&
          crmFeatures.includes(feature_to_update.feature) &&
          crmFeatures.includes(feature.feature)
        ) {
          if (feature.enabled) {
            const disabledFeature = feature.feature;
            toast.info(
              `${disabledFeature} disabled!`,
              'Only one CRM integration allowed at a time',
            );
          }
          return {
            ...feature,
            enabled: false,
          };
        }

        // If enabling a CMP feature, disable all other CMP features
        if (
          checked &&
          cmpFeatures.includes(feature_to_update.feature) &&
          cmpFeatures.includes(feature.feature)
        ) {
          if (feature.enabled) {
            const disabledFeature = feature.feature;
            toast.info(
              `${disabledFeature} disabled!`,
              'Only one CMP integration allowed at a time',
            );
          }
          return {
            ...feature,
            enabled: false,
          };
        }

        // Return the feature unchanged if none of the above conditions are met
        return feature;
      });
      setFeatures(updatedFeatures);
    }
  };
  const updateFeatures = () => {
    let isMillerHeimanEnabled = false;
    let isAiSignalRefreshEnabled = false;
    const featurePayloadData = features
      .filter((feature) => {
        const currentFeature = initialFeatures.find((item) => item.featureId == feature.featureId);
        if (currentFeature?.feature === 'Data Enrichment' && !feature.enabled) {
          localStorage.removeItem('dataEnrichmentFeature');
        }
        if (currentFeature?.feature === 'Miller Heiman Roles' && !currentFeature?.enabled && feature.enabled) {
          isMillerHeimanEnabled = true;
        }
        if (currentFeature?.feature === 'AI Signal - Auto Refresh' && (currentFeature?.enabled !== feature.enabled)) {
          isAiSignalRefreshEnabled = true;
        }
        if (
          currentFeature?.enabled != feature.enabled ||
          currentFeature?.isSproutsAdmin != feature?.isSproutsAdmin
        ) {
          return feature;
        }
      })
      .map((feature) => {
        return {
          featureId: feature.featureId,
          isEnabled: feature.enabled,
          isSproutsAdmin: feature.isSproutsAdmin,
          nameOfFeature: feature.feature,
        };
      });
    const payload = {
      uniqueID: customerId,
      emailId: emailId,
      features: featurePayloadData,
    };
    apiClient
      .post(UPDATE_CUSTOMER_WEB_TRACKING_STATUS, payload, {
        headers: {
          'X-Login-Email': emailId,
        },
      })
      .then((response) => {
        const features = response.data;
        if (features[0].Status == 200) {
          if (orderedFeatures?.length > 0) updateTheColumnsOrder();
          if (isMillerHeimanEnabled) triggerMillerHeiman();
          if (isAiSignalRefreshEnabled) triggerAiSignalRefresh();
          toast.success('Success', `${features[0].Message}`);
        } else {
          toast.error('Error', `${features[0].Message}`);
        }
        getFeatures();
      })
      .catch(() => {
        toast.error('Error', 'Something went Wrong');
      });
  };
  const getFeatures = () => {
    apiClient
      .post(
        GET_FEATURES,
        {
          emailId,
          customerId,
        },
        {
          headers: {
            'X-Login-Email': emailId,
          },
        },
      )
      .then((response) => {
        const uniqueIds = new Set();
        const uniqueObjects = [];
        for (const obj of response.data.features) {
          if (!uniqueIds.has(obj.featureId)) {
            uniqueIds.add(obj.featureId);
            uniqueObjects.push(obj);
          }
        }
        setFeatures(uniqueObjects);
        setInitialFeatures(uniqueObjects);
        const features: FeatureItem[] = response.data.features || [];
        if (features.length > 0) {
          const newFeaturesData = transformFeaturesForRedux(features);
          dispatch(setFeatureFlags(newFeaturesData.features));
          dispatch(setAdminFeatureFlags(newFeaturesData.adminFeatures));
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };
  const getFeaturesByName = (featuresName: string[]) => {
    const result: { [key: string]: boolean } = {};
    featuresName.forEach((featureName: string) => {
      const founfFeature = features.find((feature) => feature.feature === featureName);
      if (founfFeature) {
        result[featureName] = founfFeature.enabled;
      }
    });
    return result;
  };
  const triggerMillerHeiman = () => {
    apiClient.post(`${MILLER_HEIMAN_ROLE_BASE_URL}/enrich-contacts-miller-heiman-roles`, {}, {
      headers: {
        'X-Login-Email': emailId,
        'X-Customer-Id': customerId
      }
    })
  }
  const triggerAiSignalRefresh = () => {
    apiClient.post(AI_SIGNAL_REFRESH_TOOGLE, {}, {
      headers: {
        'X-Login-Email': emailId,
        'X-Customer-Id': customerId
      }
    })
  }
  return (
    <FeaturesContext.Provider
      value={{
        features,
        getFeatures,
        featureMutate,
        enableSproutsAdminFeature,
        updateFeatures,
        isFeatureUpdated,
        getFeaturesByName,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (context === undefined) {
    throw new Error('useFeatures was used outside of its Provider');
  }
  return context;
};
